import { isSuccess } from '@/context/services/type';
import { isCommonError, isObjectRecord } from '@/libs/typeguard/isError';
import { useQueryClient } from '@tanstack/react-query';
import {
  DeleteInvoiceReportViewersRequest,
  GetInvoiceReportViewersResponse,
  PutInvoiceReportViewersRequest,
  getGetInvoiceReportViewersQueryKey,
  useDeleteInvoiceReportViewers,
  useGetInvoiceReportViewersSuspense,
  usePutInvoiceReportViewers,
} from 'ap-openapi';
import { useCallback } from 'react';

/**
 * The share drawer's UI components aren't allowed to import `ap-openapi`
 * directly (lint-enforced), so the types they need are re-exported here
 * from the service layer.
 */
export type { OfficeMember } from 'ap-openapi';

/**
 * Pulls the message(s) out of the `{ ...body, statusCode }` a mutation
 * throws. Narrows with the existing type guards only, so no type
 * assertion is needed.
 */
export const readViewerErrorMessages = (
  error: unknown
): string[] | string | undefined => {
  if (!isObjectRecord(error)) return undefined;
  if (!isCommonError(error)) return undefined;
  const { messages } = error;
  if (typeof messages === 'string') return messages;
  if (Array.isArray(messages)) {
    return messages.filter(
      (message): message is string => typeof message === 'string'
    );
  }
  return undefined;
};

export type InvoiceReportViewersResult = {
  data?: GetInvoiceReportViewersResponse;
  error?: {
    messages?: string[] | string;
    code?: string;
  };
};

/**
 * The share drawer's viewer list. Same shape as ApprovalRoute.service: HTTP
 * errors are returned as `error` instead of being thrown, so the calling
 * hook can own the toast display.
 */
export const useInvoiceReportViewers = (
  reportId: string
): InvoiceReportViewersResult => {
  const { data, error } = useGetInvoiceReportViewersSuspense(reportId);

  if (error) throw error;

  const body = data?.data;
  if (!isSuccess(data?.status)) {
    if (typeof body === 'object' && isCommonError(body)) {
      return {
        error: {
          messages: body?.messages,
          code: data?.status.toString(),
        },
      };
    }
    return { error: { messages: undefined, code: data?.status.toString() } };
  }

  return { data: body };
};

/**
 * PUT / DELETE both return 204 No Content, so on success we invalidate the
 * cache and refetch the list instead of writing the response back directly.
 */
const useInvalidateViewersCache = (reportId: string) => {
  const queryClient = useQueryClient();
  return useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: getGetInvoiceReportViewersQueryKey(reportId),
      }),
    [queryClient, reportId]
  );
};

export const usePutInvoiceReportViewersService = (reportId: string) => {
  const { mutateAsync: _mutateAsync, isPending } = usePutInvoiceReportViewers();
  const invalidateCache = useInvalidateViewersCache(reportId);
  const mutateAsync = useCallback(
    async (data: PutInvoiceReportViewersRequest) => {
      const resp = await _mutateAsync({ id: reportId, data });
      if (!isSuccess(resp.status)) {
        const errorData =
          typeof resp.data === 'object' && resp.data !== null ? resp.data : {};
        throw { ...errorData, statusCode: resp.status };
      }
      await invalidateCache();
    },
    [_mutateAsync, reportId, invalidateCache]
  );
  return { mutateAsync, isPending };
};

export const useDeleteInvoiceReportViewersService = (reportId: string) => {
  const { mutateAsync: _mutateAsync, isPending } =
    useDeleteInvoiceReportViewers();
  const invalidateCache = useInvalidateViewersCache(reportId);
  const mutateAsync = useCallback(
    async (data: DeleteInvoiceReportViewersRequest) => {
      const resp = await _mutateAsync({ id: reportId, data });
      if (!isSuccess(resp.status)) {
        const errorData =
          typeof resp.data === 'object' && resp.data !== null ? resp.data : {};
        throw { ...errorData, statusCode: resp.status };
      }
      await invalidateCache();
    },
    [_mutateAsync, reportId, invalidateCache]
  );
  return { mutateAsync, isPending };
};
